import {
  Card,
  Divider,
  Switch,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import useHasPermission from '../../../hooks/useHasPermission';
import { formatCurrency } from '../../../utils';
import OnOffTypography from '../OnOffTypography';
import SetupGridItem from '../SetupGridItem';
import { ASSET_PROTECT, InsuranceTypes } from '../IntegrationsSetup/constants';
import { EditedByBlock } from '../IntegrationsSetup/customStyles';
import { InsuranceFeeInput } from '../IntegrationsSetup/InsuranceFeeInput';
import { confirmInsuranceModal } from '../IntegrationsSetup/InsuranceModal';
import integrationMessages from '../IntegrationsSetup/messages';
import messages from './messages';

type Props = {
  propertyDetails: Object,
  editMode: boolean,
  onChange: Function,
  disabled: boolean,
  intl: Object,
  selectedPropertyTransactionCodes: Array<Object>,
};

const InsuranceTypeOptions = () =>
  InsuranceTypes.map((type) => (
    <option key={`insurance-type-${type}`} value={type}>
      {type}
    </option>
  ));

const InsuranceTooltipMessage = () => (
  <Typography>
    <strong>
      <FormattedMessage {...integrationMessages.assetProtect} />:
    </strong>{' '}
    <FormattedMessage {...integrationMessages.assetProtectInfo} />
    <br />
    <br />
    <strong>
      <FormattedMessage {...integrationMessages.none} />:
    </strong>{' '}
    <FormattedMessage {...integrationMessages.noneInfo} />
  </Typography>
);

const InsuranceSetup = (props: Props) => {
  const {
    propertyDetails,
    editMode: editModeProp,
    disabled,
    onChange,
    intl,
    selectedPropertyTransactionCodes,
  } = props;
  const {
    config = {},
    insuranceType,
    insuranceFeeAmount,
    insuranceMasterTransactionCodeId,
    propertyRiskManagement = [],
    propertyType,
  } = propertyDetails;
  const allowThirdPartyInsurancePolicies =
    config.allowThirdPartyInsurancePolicies ?? false;

  const [lastUpdatedPropertyRiskManagement] = propertyRiskManagement;

  const {
    riskMgmtAssetProtectAdmin,
    assetProtectLogic,
    allowThirdPartyRentersInsurancePolicy,
    allow3RdPartyRentersInsurancePolicy,
  } = useFlags();

  const showThirdPartyToggle =
    allowThirdPartyRentersInsurancePolicy ||
    allow3RdPartyRentersInsurancePolicy;

  const hasInsuranceEnrollmentPermission = useHasPermission(
    'insurance-enrollment',
  );
  const isCommercialProperty = propertyType === 'Commercial';

  const editMode = editModeProp && !disabled;

  const handleSwitchChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.checked;
    onChange({ target: { name, value } });
  };

  const onInsuranceTypeChange = useCallback(
    (event) => {
      const { name, value } = event.target;

      confirmInsuranceModal(intl, value).then((modalValues) => {
        onChange({ target: { name, value } });

        onChange({
          target: {
            name: 'insuranceFeeAmount',
            value: modalValues.insuranceFee ?? null,
          },
        });
      });
    },
    [intl, onChange],
  );

  return (
    <Card sx={{ padding: 2, height: '100%' }}>
      <Typography variant={'titleLarge'}>
        <FormattedMessage {...messages.insurance} />
      </Typography>

      <Divider sx={{ marginY: 2 }} />

      <Grid container spacing={2}>
        {showThirdPartyToggle && (
          <SetupGridItem
            label={
              <FormattedMessage
                {...messages.allowThirdPartyInsurancePolicies}
              />
            }
            TooltipProps={{
              title: (
                <FormattedMessage
                  {...messages.allowThirdPartyInsurancePoliciesTooltip}
                />
              ),
              children: <AlertInfoIcon />,
            }}
          >
            {editMode ? (
              <Switch
                name="config.allowThirdPartyInsurancePolicies"
                id="allowThirdPartyInsurancePolicies-switch"
                checked={allowThirdPartyInsurancePolicies}
                disabled={!editMode}
                onChange={handleSwitchChange}
                inputProps={{
                  'data-checked': allowThirdPartyInsurancePolicies,
                }}
              />
            ) : (
              <OnOffTypography on={allowThirdPartyInsurancePolicies}>
                {allowThirdPartyInsurancePolicies ? (
                  <FormattedMessage {...integrationMessages.on} />
                ) : (
                  <FormattedMessage {...integrationMessages.off} />
                )}
              </OnOffTypography>
            )}
          </SetupGridItem>
        )}

        {riskMgmtAssetProtectAdmin && !isCommercialProperty && (
          <>
            <SetupGridItem
              label={
                <FormattedMessage
                  {...integrationMessages.insuranceEnrollment}
                />
              }
              TooltipProps={{
                title: <InsuranceTooltipMessage />,
                children: <AlertInfoIcon />,
              }}
            >
              {editMode ? (
                <select
                  name="insuranceType"
                  className="form-control"
                  value={insuranceType}
                  onChange={onInsuranceTypeChange}
                  disabled={!hasInsuranceEnrollmentPermission}
                >
                  <InsuranceTypeOptions />
                </select>
              ) : (
                insuranceType
              )}
            </SetupGridItem>
            {insuranceType === ASSET_PROTECT && (
              <SetupGridItem
                label={
                  <FormattedMessage {...integrationMessages.assetProtectFee} />
                }
              >
                {editMode ? (
                  <InsuranceFeeInput
                    insuranceFeeAmount={insuranceFeeAmount}
                    onChange={onChange}
                    intl={intl}
                  />
                ) : (
                  formatCurrency(intl, insuranceFeeAmount)
                )}
              </SetupGridItem>
            )}
            {insuranceType === ASSET_PROTECT && assetProtectLogic && (
              <SetupGridItem
                label={
                  <FormattedMessage
                    {...integrationMessages.insuranceMasterTransactionCode}
                  />
                }
              >
                {editMode ? (
                  <select
                    name="insuranceMasterTransactionCodeId"
                    className="form-control"
                    value={(() => {
                      const found = (
                        selectedPropertyTransactionCodes || []
                      ).find(
                        (c) =>
                          String(c.id) ===
                            String(insuranceMasterTransactionCodeId) ||
                          String(c.transactionCode?.id) ===
                            String(insuranceMasterTransactionCodeId),
                      );
                      return found ? found.id : 'default';
                    })()}
                    onChange={onChange}
                    disabled={!hasInsuranceEnrollmentPermission}
                  >
                    <option value="default" disabled>
                      {intl.formatMessage(integrationMessages.none)}
                    </option>
                    {(selectedPropertyTransactionCodes || []).map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.transactionCode?.code} -{' '}
                        {code.transactionCode?.description}
                      </option>
                    ))}
                  </select>
                ) : (
                  (() => {
                    const foundCode = (
                      selectedPropertyTransactionCodes || []
                    ).find(
                      (code) =>
                        String(code.id) ===
                          String(insuranceMasterTransactionCodeId) ||
                        String(code.transactionCode?.id) ===
                          String(insuranceMasterTransactionCodeId),
                    );
                    return foundCode
                      ? `${foundCode.transactionCode?.code} - ${foundCode.transactionCode?.description}`
                      : '---';
                  })()
                )}
              </SetupGridItem>
            )}
            {lastUpdatedPropertyRiskManagement && (
              <SetupGridItem>
                <EditedByBlock>
                  <FormattedMessage {...integrationMessages.editedBy} />{' '}
                  {lastUpdatedPropertyRiskManagement.createdBy.firstName}{' '}
                  {lastUpdatedPropertyRiskManagement.createdBy.lastName}{' '}
                  <FormattedMessage {...integrationMessages.on} />{' '}
                  {moment(lastUpdatedPropertyRiskManagement.createdAt).format(
                    'MMM DD, YYYY @ h:mm A',
                  )}
                </EditedByBlock>
              </SetupGridItem>
            )}
          </>
        )}
      </Grid>
    </Card>
  );
};

export default InsuranceSetup;
